import axiosClient from "./axiosClient";

export type ProductCategory = {
  id: string;
  name: string;
  slug: string;

  parentId: string;
  parentName: string;

  sortOrder: number;

  isActive: boolean;
  showOnMenu: boolean;

  createdAtUtc?: string;
  updatedAtUtc?: string;
};

const normalizeText = (
  value: string
) =>
  value
    .trim()
    .toLocaleLowerCase("vi");

// ==================================================
// CATEGORY API
// ==================================================

export const categoryApi = {
  // ==================================================
  // ADMIN - GET ALL
  // ==================================================

  getAll: async () => {
    const response =
      await axiosClient.get(
        "/api/admin/product-categories"
      );

    return response.data;
  },

  // ==================================================
  // ADMIN - GET PARENTS
  // ==================================================

  getParents: async () => {
    const response =
      await axiosClient.get(
        "/api/admin/product-categories/parents"
      );

    return response.data;
  },

  // ==================================================
  // ADMIN - GET CHILDREN
  // ==================================================

  getChildren: async (
    parentId: string
  ) => {
    const response =
      await axiosClient.get(
        `/api/admin/product-categories/${parentId}/children`
      );

    return response.data;
  },

  // ==================================================
  // ADMIN - CREATE
  //
  // Trước khi POST sẽ kiểm tra category đã tồn tại.
  // Tránh tạo trùng và tránh Mongo unique index lỗi.
  // ==================================================

  create: async (
    name: string,
    parentId = ""
  ) => {
    const cleanName =
      name.trim();

    const cleanParentId =
      parentId.trim();

    if (!cleanName) {
      throw new Error(
        "Tên danh mục không được để trống."
      );
    }

    // ================================================
    // CHECK EXISTING FIRST
    // ================================================

    const allResponse =
      await axiosClient.get(
        "/api/admin/product-categories"
      );

    const categories:
      ProductCategory[] =
      Array.isArray(
        allResponse.data?.categories
      )
        ? allResponse.data.categories
        : [];

    const existed =
      categories.find(
        (category) =>
          normalizeText(
            category.name
          ) ===
            normalizeText(
              cleanName
            ) &&
          (
            category.parentId ||
            ""
          ) ===
            cleanParentId
      );

    if (existed) {
      return {
        message:
          "Danh mục đã tồn tại.",

        existed: true,

        category:
          existed,
      };
    }

    // ================================================
    // CREATE NEW
    //
    // Backend hiện tại đang nhận multipart/form-data
    // qua [FromForm].
    // ================================================

    const formData =
      new FormData();

    formData.append(
      "Name",
      cleanName
    );

    formData.append(
      "ParentId",
      cleanParentId
    );

    formData.append(
      "SortOrder",
      "0"
    );

    formData.append(
      "IsActive",
      "true"
    );

    formData.append(
      "ShowOnMenu",
      "true"
    );

    const response =
      await axiosClient.post(
        "/api/admin/product-categories",
        formData
      );

    return response.data;
  },

  // ==================================================
  // ADMIN - DELETE
  // ==================================================

  delete: async (
    id: string
  ) => {
    if (!id) {
      throw new Error(
        "Id danh mục không hợp lệ."
      );
    }

    const response =
      await axiosClient.delete(
        `/api/admin/product-categories/${id}`
      );

    return response.data;
  },

  // ==================================================
  // PUBLIC - GET ALL
  // ==================================================

  getPublicAll: async () => {
    const response =
      await axiosClient.get(
        "/api/product-categories"
      );

    return response.data;
  },

  // ==================================================
  // PUBLIC - GET PARENTS
  // ==================================================

  getPublicParents: async () => {
    const response =
      await axiosClient.get(
        "/api/product-categories/parents"
      );

    return response.data;
  },

  // ==================================================
  // PUBLIC - GET CHILDREN
  // ==================================================

  getPublicChildren: async (
    parentId: string
  ) => {
    const response =
      await axiosClient.get(
        `/api/product-categories/${parentId}/children`
      );

    return response.data;
  },
};

export default categoryApi;